import { Checkbox, ModalProps, Stack, TextInput } from "@mantine/core"
import { usePlanLayoutAttachmentApi } from "../../../../attachment-api/planLayoutAttachmentApi"
import { useForm } from "@mantine/form"
import { PlanLayoutAttachmentDto } from "../../../../@client/Theasoft_ApiServer_Attachments"
import { useTranslation } from "react-i18next"
import { FormButtonGroup } from "../../../../-components/form-button-group"
import {
  CancelButton,
  DeleteButton,
  SubmitButton,
} from "../../../../-components/Buttons"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../utils/form-submission-notifications"
import { DefaultModal } from "../../../../-components/default-modal"
import { useInvalidate } from "../../../../typed-client/hooks/use-invalidate"
import { openConfirmModal } from "@mantine/modals"

interface Props extends ModalProps {
  planLayoutAttachment: PlanLayoutAttachmentDto
}

interface FormValues {
  description: string
  publishOnDashboard: boolean
  showWithoutPermission: boolean
}

export function PlanLayoutAttachmentEditModal({
  planLayoutAttachment,
  ...props
}: Props) {
  const planLayoutAttachmentApi = usePlanLayoutAttachmentApi()

  const form = useForm<FormValues>({
    mode: "uncontrolled",
    initialValues: {
      description: planLayoutAttachment.description,
      publishOnDashboard: planLayoutAttachment.publishOnDashboard,
      showWithoutPermission: planLayoutAttachment.showWithoutPermission,
    },
  })

  const invalidate = useInvalidate()

  const invalidatePlanLayoutAttachment = () => {
    invalidate("/api/planLayout/attachments/GetPlanLayoutAttachments")
  }

  const handleSubmitAsync = async (values: FormValues) => {
    await planLayoutAttachmentApi
      .updateAttachmentMetadata(
        planLayoutAttachment.attachmentId,
        planLayoutAttachment.planLayoutId,
        { ...planLayoutAttachment, ...values }
      )
      .catch(showErrorNotification)
      .then(() => {
        invalidatePlanLayoutAttachment()
        showSuccessNotification()
        props.onClose?.()
      })
  }

  const handleDeleteAsync = async () => {
    openConfirmModal({
      title: t("DeletePlan"),
      centered: true,
      labels: { confirm: t("Delete"), cancel: t("Cancel") },
      confirmProps: { color: "red", variant: "light" },
      onConfirm: async () =>
        await planLayoutAttachmentApi
          .deleteAttachmentAndMetadata(
            planLayoutAttachment.attachmentId,
            planLayoutAttachment.planLayoutId
          )
          .catch(showErrorNotification)
          .then(() => {
            invalidatePlanLayoutAttachment()
            showSuccessNotification()
            props.onClose?.()
          }),
    })
  }

  const { t } = useTranslation("app_server")
  return (
    <DefaultModal {...props} title={t("EditPlan")}>
      <form onSubmit={form.onSubmit(handleSubmitAsync)}>
        <Stack>
          <TextInput
            label={t("Description")}
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
          <Checkbox
            label={t("PublishOnDashboard")}
            key={form.key("publishOnDashboard")}
            {...form.getInputProps("publishOnDashboard", {
              type: "checkbox",
            })}
          />
          <Checkbox
            label={t("ShowWithoutPermission")}
            key={form.key("showWithoutPermission")}
            {...form.getInputProps("showWithoutPermission", {
              type: "checkbox",
            })}
          />
          <FormButtonGroup>
            <DeleteButton onClick={handleDeleteAsync} />
            <CancelButton onClick={props.onClose} />
            <SubmitButton>{t("Update")}</SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
